import moment from 'moment';
import { styled } from 'styled-components';
import * as uuid from 'uuid';

import { Button } from '@vivaahealth/design-system';

import * as api from '../../../../../../api';
import type { ChatConversation } from '../../../../../../common';
import MessageContent, { getMessageContent } from '../../messageContent';
import ProviderCard from '../../../../../ProviderCard';
import { Accordion } from 'react-bootstrap';

interface Props {
  message: ChatConversation;
  showTextAnimation: boolean;
  onMakePaymentClicked: () => void;
}

const PaymentBalance: React.FC<Props> = ({
  message,
  showTextAnimation,
  onMakePaymentClicked,
}) => {
  const data = {
    amount: 10000,
    totalAmount: 10000,
    dueDate: moment().add(2, 'days').toDate(),
    provider: {
      id: uuid.v4(),
      firstName: 'Charlotte',
      lastName: 'Coleman',
      prefix: null,
      suffix: 'MD',
      previsitImageUrl:
        'https://notable-providers.s3.us-east-2.amazonaws.com/Charlotte-Coleman.png',
      sex: api.User.Sex.Female,
      virtualVisitUrl: null,
    },
    practice: {
      name: 'Pacific Coast Medical Associates',
      address: '95 Park Ave',
      city: 'Palo Alto',
      state: 'CA',
      zip: '94306',
      phoneNumber: '6502032040',
      timezone: 'America/Los_Angeles',
    },
  };

  return (
    <>
      <MessageContent
        content={getMessageContent(message)}
        showTextAnimation={showTextAnimation}
        isAgent={'answer' in message}
        isLoading={!message.done}
      />
      {message.done ? (
        <>
          <div data-test-id="payment-balance">
            <CardHeaderWrapper>
              <Row $isBold>
                <span>Amount due</span>
                <span>${(data.amount / 100).toFixed(2)}</span>
              </Row>
              <Row>
                <span>Date due</span>
                <span>{moment(data.dueDate).format('MMM. DD, YYYY')}</span>
              </Row>
            </CardHeaderWrapper>
            <ProviderCard
              isOnlyOneCard={true}
              disableTopBorderRadius={true}
              provider={{
                ...data.provider,
                phoneNumber: data.practice.phoneNumber,
                practices: [data.practice],
              }}
            >
              <Accordion>
                <ViewBill eventKey="0">
                  <ViewBillButton>+ View bill details</ViewBillButton>
                  <Accordion.Body>
                    <DetailWrapper>
                      <LineItem>
                        <div>Encounter Date</div>
                        <div>{moment().format('MMM. DD, YYYY')}</div>
                      </LineItem>
                      <h4>Bill details</h4>
                      <LineItem>
                        <div>Mammography</div>
                        <div>$1,000.00</div>
                      </LineItem>
                      <LineItem>
                        <div>Lab work</div>
                        <div>$134.00</div>
                      </LineItem>
                      <LineItem>
                        <div>Office visit</div>
                        <div>$100.00</div>
                      </LineItem>
                      <LineItem $isBold>
                        <div>Total charge amount</div>
                        <div>$1,234.00</div>
                      </LineItem>
                      <LineItem>
                        <div>BCBS discount</div>
                        <div>-$1,000.00</div>
                      </LineItem>
                      <LineItem>
                        <div>BCBS paid amount</div>
                        <div>-$134.00</div>
                      </LineItem>
                      <LineItem $isBold $hideDelimiter>
                        <div>Amount due</div>
                        <div>$100.00</div>
                      </LineItem>
                    </DetailWrapper>
                  </Accordion.Body>
                </ViewBill>
              </Accordion>
            </ProviderCard>
          </div>
          <Total>
            <Row $isBold>
              <span>Total balance due</span>
              <div>${(data.totalAmount / 100).toFixed(2)}</div>
            </Row>
          </Total>
          <ActionWrapper>
            <ActionButton
              onClick={() => onMakePaymentClicked()}
              disabled={message.disabled}
              data-test-id="payment-balance-make-payment"
            >
              Make payment
            </ActionButton>
          </ActionWrapper>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

const Total = styled.div`
  background-color: var(--color-shade-30);
  border: 1px solid var(--border-color);
  border-radius: var(--button-border-radius);
  padding: 5px 20px;
  margin-top: 15px;
`;

const CardHeaderWrapper = styled.div`
  background-color: var(--color-shade-30);
  border: 1px solid var(--border-color);
  border-bottom: none;
  padding: 5px 20px;
  border-radius: var(--button-border-radius) var(--button-border-radius) 0 0;
`;

const Row = styled.div.attrs<{ $isBold?: boolean }>((props) => ({
  $isBold: props.$isBold ?? false,
}))`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-weight: ${(props) => (props.$isBold ? 'bold' : 'normal')};
  font-size: ${(props) => (props.$isBold ? '16px' : '14px')};
`;

const ViewBill = styled(Accordion.Item)`
  border: none;
`;

const ViewBillButton = styled(Accordion.Header)`
  width: 100%;
  border: 1px solid var(--action-color) !important;
  color: var(--action-color) !important;
  background-color: transparent !important;
  margin-top: 15px !important;
  font-size: 16px !important;
  border-radius: var(--button-border-radius);

  > button {
    padding: 10px 0 !important;
    text-align: center;
    display: block;
    color: var(--action-color) !important;
    background-color: var(--color-shade-0) !important;
    box-shadow: none !important;

    &::after {
      display: none;
    }
  }
`;

const ActionWrapper = styled.div`
  margin-top: 20px;
`;

const ActionButton = styled(Button)`
  width: 100%;
  background-color: var(--action-color) !important;
`;

const DetailWrapper = styled.div`
  font-size: 14px;
  color: var(--color-shade-70);

  > h4 {
    font-size: 14px;
    margin-top: 15px;
    font-weight: bold;
    color: var(--font-color);
  }
`;

const LineItem = styled.div.attrs<{
  $isBold?: boolean;
  $hideDelimiter?: boolean;
}>((props) => ({
  $isBold: props.$isBold ?? false,
  $hideDelimiter: props.$hideDelimiter ?? false,
}))`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-bottom: 8px;
  padding-top: 5px;
  border-bottom: ${(props) =>
    props.$hideDelimiter ? 'none' : '1px solid var(--border-color)'};
  font-weight: ${(props) => (props.$isBold ? 'bold' : 'normal')};
`;

export default PaymentBalance;
